import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { useTheme } from '../../../theme';
import { AeroexpressTrip, AeroexpressSelectedTrip } from '@websky/graphql';
import { useAeroexpressState } from './hooks';
import Disclaimer from '../../../Disclaimer/Disclaimer';
import AdditionalServiceWrapper from '../../../AdditionalServiceWrapper';
import AeroexpressSegment from '../AeroexpressSegment/AeroexpressSegment';
import { CdnIcon } from '@shared/ui';

export interface Props {
	trips: AeroexpressTrip[];
	selectedTrips: AeroexpressSelectedTrip[];
	isLoading?: boolean;
	onSubmit: (ids: string[]) => void;
	onClose: () => void;
}

const AeroexpressPopup: React.FC<Props> = ({ trips, selectedTrips, isLoading, onClose, onSubmit }) => {
	const { AeroexpressPopup: css } = useTheme('CheckoutAdditionalService');
	const { t } = useTranslation('Checkout');

	const {
		state,
		tripsByDirection,
		selectedTripItems,
		totalPrice,
		allPassengers,
		hasChanges,
		setTrip,
		clearTrips
		// toggleAllPassengers
	} = useAeroexpressState(trips, selectedTrips);

	const onSubmitHandler = () => {
		const selectedTripsIds = selectedTrips.map(trip => trip.id);
		const selectedTripsItemIds = selectedTripItems.map(trip => trip.id);

		if (JSON.stringify(selectedTripsIds.sort()) !== JSON.stringify(selectedTripsItemIds.sort())) {
			onSubmit(selectedTripsItemIds);
		} else {
			onClose();
		}
	};

	const onClear = () => {
		clearTrips();

		if (selectedTrips.length) {
			onSubmit([]);
		}
	};

	return (
		<AdditionalServiceWrapper
			className={css.root}
			headerClassName={css.header}
			textClassName={css.text}
			contentClassName={css.content}
			footerClassName={css.footer}
			header={t('Aeroexpress')}
			text={
				<>
					<p>{t('No worries about traffic jams, arrival at the airport strictly on schedule.')}</p>
					<p>{t('Aeroexpress tickets are valid for 30 days.')}</p>
					<div className={css.info}>
						<div className={css.disclaimer}>
							<a href="https://aeroexpress.ru" target="_blank">
								<Disclaimer className={css.content_disclaimer} icon={<CdnIcon icon="Info/Warning" />}>
									{t('Aeroexpress:View schedule')}
								</Disclaimer>
							</a>
						</div>

						{/* TODO: uncomment this when php was ready */}
						{/*<Switch*/}
						{/*	mainClassName={css.toggle}*/}
						{/*	label={t('Passengers')}*/}
						{/*	isActive={allPassengers}*/}
						{/*	onClick={toggleAllPassengers}*/}
						{/*/>*/}
					</div>
				</>
			}
			totalPrice={totalPrice}
			isLoading={isLoading}
			onConfirm={onSubmitHandler}
			onClear={onClear}
			onCancel={onClose}
			isConfirmDisabled={!hasChanges}
		>
			<div className={css.segments}>
				{tripsByDirection.map(([direction, trips]) => (
					<AeroexpressSegment
						key={direction}
						state={state}
						direction={direction}
						trips={trips}
						allPassengers={allPassengers}
						onChange={setTrip}
					/>
				))}
			</div>
		</AdditionalServiceWrapper>
	);
};

export default AeroexpressPopup;
